import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import PubBadge from "../../components/PubBadge";
import { toast } from "sonner";
import { Check, X } from "lucide-react";

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/admin/dashboard");
      setData(r.data);
      setLoadError("");
    } catch {
      setLoadError("Could not load the dashboard. Please sign in again or refresh the page.");
    }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="p-8 text-sm text-[#5c5852]">{loadError || "Loading…"}</div>;

  return (
    <div className="p-6 lg:p-10" data-testid="admin-dashboard">
      <div className="mb-8">
        <p className="btw-overline mb-1">Overview</p>
        <h1 className="font-serif text-3xl">Dashboard</h1>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Recently Published" testid="panel-recent">
          {data.recent_completed.length === 0 && <Empty>No published stories yet.</Empty>}
          {data.recent_completed.map((e) => (
            <Row key={e.id}>
              <Link to={`/admin/events/${e.id}`} className="font-medium hover:text-[#876a4f]" data-testid={`recent-${e.id}`}>
                {e.event_name}
              </Link>
              <PubBadge pub={e.publication} />
            </Row>
          ))}
        </Panel>

        <Panel title="In Progress" testid="panel-pending">
          {data.pending_events.length === 0 && <Empty>Nothing in progress.</Empty>}
          {data.pending_events.map((e) => (
            <Row key={e.id}>
              <Link to={`/admin/events/${e.id}`} className="font-medium hover:text-[#876a4f]">
                {e.event_name}
              </Link>
              <span className="text-xs text-[#5c5852]">{e.status}</span>
            </Row>
          ))}
        </Panel>

        <Panel title="Top Vendors" testid="panel-top-vendors">
          {data.top_vendors.length === 0 && <Empty>No vendor data yet.</Empty>}
          {data.top_vendors.map((v) => (
            <Row key={v.id}>
              <span className="font-medium">{v.name}</span>
              <span className="text-xs text-[#5c5852]">
                {v.type} · {v.event_count} event{v.event_count !== 1 ? "s" : ""}
              </span>
            </Row>
          ))}
        </Panel>
      </div>
    </div>
  );
}

const Panel = ({ title, children, testid }) => (
  <section className="bg-white border border-[#dcd3c6] p-5" data-testid={testid}>
    <h2 className="btw-overline mb-4">{title}</h2>
    <div className="divide-y divide-[#dcd3c6]/60">{children}</div>
  </section>
);
const Row = ({ children }) => (
  <div className="flex items-center justify-between py-3 first:pt-0 last:pb-0">{children}</div>
);
const Empty = ({ children }) => (
  <div className="text-sm text-[#5c5852] italic py-2">{children}</div>
);
const FieldDot = ({ label, ok }) => (
  <span className="inline-flex items-center gap-1">
    {ok ? <Check size={12} className="text-[#768c6a]" /> : <X size={12} className="text-[#b35c5c]" />}
    {label}
  </span>
);
